import Api, { type ApiRequestOptions } from '../util/Api';
import { isPlainObject, type JsonRecord } from '../util/json-merge-patch';

/**
 * Transport for the per-user UX settings document (API-617). Both routes are v3, act on the
 * session user only (no user id anywhere) and answer the full document under the annotated
 * envelope's `.data`.
 *
 * `Api.fetch` never rejects: a failure comes back as `ok: false`. Both functions turn that, and a
 * `.data` that is not a JSON object, into `undefined`, so a caller has exactly one failure to
 * handle and can never mistake a missing `.data` for an empty document.
 */
export const USER_SETTINGS_URL = 'users/settings';

/**
 * The user a request made right now would act on: the session's effective user, `''` when signed
 * out. The store checks it before every request, because the routes carry no user id: a write
 * queued for one user must not go out once the session belongs to another (Login As can switch the
 * session in place, and `Api` holds the new token before React has re-rendered).
 */
export function sessionUserId(): string {
    const userId = Api.user?.userId;
    return userId === undefined || userId === null ? '' : String(userId);
}

/** `GET users/settings`: the user's document, `{}` when they have none, `undefined` on failure. */
export async function fetchUserSettings(): Promise<JsonRecord | undefined> {
    const response = await Api.get<undefined, unknown>(USER_SETTINGS_URL);
    return response.ok && isPlainObject(response.data) ? response.data : undefined;
}

/**
 * `PATCH users/settings` with a JSON Merge Patch (RFC 7396). Resolves to the full merged document,
 * or `undefined` when the write failed.
 */
export async function patchUserSettings(
    patch: JsonRecord,
    opts?: ApiRequestOptions
): Promise<JsonRecord | undefined> {
    const response = await Api.patch<JsonRecord, unknown>(USER_SETTINGS_URL, patch, false, opts);
    return response.ok && isPlainObject(response.data) ? response.data : undefined;
}
